import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

/**
 * Thin service for the `ai_sessions` table — used by the new agent runtime.
 * Distinct from ChatSessionService (which targets the legacy `chat_sessions`
 * table). The conversation action menus (sidebar recent-chats rows + create
 * breadcrumb) surface rename/archive/attach/delete here so callers stay out of
 * the no-restricted-imports trap on the supabase client.
 */
class AiSessionServiceClass extends EnhancedBaseService {
  async renameSession(sessionId: string, title: string): Promise<ServiceResponse<boolean>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { error } = await this.client
        .from('ai_sessions')
        // title_is_manual pins the user's own name: auto-titling re-derives on
        // slash-command turns and used to overwrite a rename, because it only
        // checked whether a title existed, not who wrote it.
        .update({ title, title_is_manual: true })
        .eq('id', sessionId)
        .eq('user_id', userId);
      if (error) throw new Error(`Rename failed: ${error.message}`);
      return true;
    });
  }

  async deleteSession(sessionId: string): Promise<ServiceResponse<boolean>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { error } = await this.client
        .from('ai_sessions')
        .delete()
        .eq('id', sessionId)
        .eq('user_id', userId);
      if (error) throw new Error(`Delete failed: ${error.message}`);
      return true;
    });
  }

  /** Archive a conversation without deleting it or breaking its project link. */
  async archiveSession(sessionId: string): Promise<ServiceResponse<boolean>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('ai_sessions')
        .update({ status: 'archived', archived_at: new Date().toISOString() })
        .eq('id', sessionId)
        .eq('user_id', userId)
        .select('id')
        .maybeSingle();
      if (error) throw new Error(`Status update failed: ${error.message}`);
      if (!data) throw new Error('Status update failed: conversation was not found');
      return true;
    });
  }

  /** Restore an archived conversation to the same history and project context. */
  async restoreSession(sessionId: string): Promise<ServiceResponse<boolean>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('ai_sessions')
        .update({ status: 'active', archived_at: null })
        .eq('id', sessionId)
        .eq('user_id', userId)
        .select('id')
        .maybeSingle();
      if (error) throw new Error(`Status update failed: ${error.message}`);
      if (!data) throw new Error('Status update failed: conversation was not found');
      return true;
    });
  }

  /** @deprecated Prefer archiveSession or restoreSession for explicit semantics. */
  async setSessionStatus(
    sessionId: string,
    status: 'active' | 'archived',
  ): Promise<ServiceResponse<boolean>> {
    return status === 'archived' ? this.archiveSession(sessionId) : this.restoreSession(sessionId);
  }

  /**
   * Link (or unlink, when `planId` is null) a conversation to a project.
   *
   * `ai_sessions.project_id` is the FK the AI-SDK-v7 chat surfaces actually
   * read (`useProjectConversations`, `useAiMessagesByPlan`), but the legacy
   * `chat_sessions` table still backs some brand-voice/session-sync surfaces
   * during the migration window — so we write BOTH, mirroring
   * `useSaveToProject`. Either write failing alone is tolerated; both failing
   * surfaces an error.
   */
  async attachToPlan(sessionId: string, planId: string | null): Promise<ServiceResponse<boolean>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const [aiResult, chatResult] = await Promise.all([
        this.client
          .from('ai_sessions')
          .update({ project_id: planId, updated_at: new Date().toISOString() })
          .eq('id', sessionId)
          .eq('user_id', userId),
        this.client.from('chat_sessions').update({ project_id: planId }).eq('id', sessionId),
      ]);
      if (aiResult.error && chatResult.error) {
        throw new Error(`Link session failed: ${aiResult.error.message}`);
      }
      return true;
    });
  }
}

export const aiSessionService = new AiSessionServiceClass();
